import React from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import AppLayout from './AppLayout'
import AppLayoutPublic from './AppLayoutPublic'
import PageNotFound from './pages/PageNotFound'
import { PurpleMenuItem, PurpleNotification } from '../data/interfaces'
import { usePurple } from './PurpleContext'
import { Security, LoginCallback } from '@okta/okta-react'

interface PurpleAppProps {
  children: JSX.Element | JSX.Element[]
  menuItems: PurpleMenuItem[]
  notifications: PurpleNotification[]
}

const PurpleApp = (props: PurpleAppProps) => {
  const { children, menuItems, notifications } = props
  const navigate = useNavigate()
  const purpleContext = usePurple()

  purpleContext.setMenu(menuItems)
  purpleContext.setNotifications(notifications)

  const requestedScopes = ['openid', 'profile', 'email']

  const authClient = new OktaAuth({
    clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
    issuer: process.env.REACT_APP_OKTA_ISSUER,
    redirectUri: process.env.REACT_APP_OKTA_REDIRECT_URI,
    scopes: requestedScopes,
    responseType: ['token', 'id_token'],
    pkce: true
    // disableHttpsCheck: OKTA_TESTING_DISABLEHTTPSCHECK,
  })

  const triggerLogin = async () => {
    await authClient.signInWithRedirect()
  }

  const customAuthHandler = async () => {
    const previousAuthState = authClient.authStateManager.getPreviousAuthState()
    if (!previousAuthState || !previousAuthState.isAuthenticated) {
      // App initialization stage
      await triggerLogin()
    }
  }

  const restoreOriginalUri = async (
    _oktaAuth: OktaAuth,
    originalUri: string
  ) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin))
  }

  return (
    <React.StrictMode>
      <Security
        oktaAuth={authClient}
        onAuthRequired={customAuthHandler}
        restoreOriginalUri={restoreOriginalUri}
      >
        <Routes>
          <Route
            path='authorization-code/callback'
            element={<LoginCallback />}
          />
          <Route path='/' element={<AppLayout />}>
            {children}
          </Route>
          <Route element={<AppLayoutPublic />}>
            <Route path='*' element={<PageNotFound />} />
          </Route>
        </Routes>
      </Security>
    </React.StrictMode>
  )
}

export default PurpleApp
